/**
 * Small matrix/geometry readers used by the transform handles.
 *
 * Matrix *construction* lives in entityTransform.ts — this file used to carry a
 * parallel set of builders (createTranslationMatrix, createRotationMatrix,
 * createScaleMatrix, createUniformScaleMatrix, copyMatrix, invertMatrix,
 * multiplyMatrices, createIdentityMatrix, calculateAngle) that no call site ever
 * imported. They were removed because they were both dead and unsafe: the
 * translation builder leaked its OdGeVector3d on every call, and the scale
 * builder leaked three matrices, a scale object and a point. Worse, its name
 * collided with the live, correct createTranslationMatrix in entities/cloning.ts,
 * so anyone refactoring across the two could silently pick the leaking one.
 *
 * OdGeMatrix3d is a 4x4 transformation matrix stored in row-major order.
 */

import type { Point3D } from "./types";

/**
 * Extract matrix translation as a simple array [x, y, z].
 * Full matrix extraction not available in this API, so we store translation.
 */
export function matrixToArray(
  matrix: import("../types/DrawingWeb").DrawingModule.OdGeMatrix3d,
): number[] {
  // Get translation component from matrix
  const translation = matrix.translation1();
  return [translation.x, translation.y, translation.z];
}

/**
 * Calculate distance between two points.
 */
export function distance(p1: Point3D, p2: Point3D): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dz = p2.z - p1.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate 2D distance between two points.
 */
export function distance2D(p1: Point3D, p2: Point3D): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}
